import React, { useEffect, useState } from "react";
import { BACKEND_URL } from "@/lib/api";
import { Image as ImageIcon, Table as TableIcon, BarChart3, Trash2, AlignLeft, AlignCenter, AlignRight, Loader2 } from "lucide-react";

/** Authenticated <img> for attachment previews (bearer token cannot ride on a plain src). */
export function AttImage({ workspaceId, docId, attId, className, alt }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let dead = false; let created = null;
    (async () => {
      try {
        const token = localStorage.getItem("fx_token");
        const r = await fetch(`${BACKEND_URL}/api/workspaces/${workspaceId}/documents/${docId}/attachments/${attId}/preview`, { headers: { Authorization: `Bearer ${token}` } });
        if (!r.ok || dead) return;
        created = URL.createObjectURL(await r.blob());
        setUrl(created);
      } catch { /* preview unavailable */ }
    })();
    return () => { dead = true; if (created) URL.revokeObjectURL(created); };
  }, [workspaceId, docId, attId]);
  if (!url) return <div className="flex items-center justify-center py-6 text-slate-400 text-xs"><Loader2 size={14} className="animate-spin mr-2" />Loading image…</div>;
  return <img src={url} alt={alt || ""} className={className} />;
}

const WIDTHS = [25, 50, 75, 100];

/** One structured block (image / chart / table) rendered inside the A4 review canvas. */
export function AttachmentBlock({ att, index, total, sections, workspaceId, docId, canEdit, onUpdate, onMove, onDelete, selected, onSelect }) {
  const align = att.align || "center";
  const pct = att.width_pct || 100;
  const justify = align === "left" ? "flex-start" : align === "right" ? "flex-end" : "center";
  const isTable = att.type === "table";
  const label = isTable ? `Table` : `Figure`;

  return (
    <div
      onClick={() => onSelect?.(att.id)}
      className={`my-3 rounded transition ${selected ? "ring-2 ring-[#1478FF] ring-offset-2" : canEdit ? "hover:ring-1 hover:ring-slate-300" : ""}`}
      data-testid={`block-${att.id}`}
    >
      <div style={{ display: "flex", justifyContent: justify }}>
        <div style={{ width: `${pct}%` }}>
          {isTable ? (
            <table className="meta" style={{ width: "100%" }} data-testid={`block-table-${att.id}`}>
              <thead>
                <tr>{(att.columns || []).map((c, i) => <th key={i} style={{ textAlign: "left", background: "#f1f5f9", padding: "4px 6px", border: "1px solid #cbd5e1", fontSize: 11 }}>{c}</th>)}</tr>
              </thead>
              <tbody>
                {(att.rows || []).map((r, ri) => (
                  <tr key={ri}>{(att.columns || []).map((_, ci) => <td key={ci} style={{ padding: "4px 6px", border: "1px solid #cbd5e1", fontSize: 11 }}>{r[ci] || ""}</td>)}</tr>
                ))}
              </tbody>
            </table>
          ) : (
            <AttImage workspaceId={workspaceId} docId={docId} attId={att.id} alt={att.caption} className="w-full rounded" />
          )}
          <div style={{ textAlign: align, fontSize: 10, fontStyle: isTable ? "normal" : "italic", color: "#475569", marginTop: 4 }} data-testid={`block-caption-${att.id}`}>
            {label} {index}{att.caption ? `. ${att.caption}` : ""}
          </div>
        </div>
      </div>

      {canEdit && selected && (
        <div className="mt-2 bg-[#0D1224] border border-[#1E293B] rounded-lg p-2 space-y-2" onClick={(e) => e.stopPropagation()} data-testid={`block-toolbar-${att.id}`}>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] uppercase tracking-widest text-slate-500">Move</span>
            <select
              value={att.section_id || ""}
              onChange={(e) => onUpdate(att.id, { section_id: e.target.value || null })}
              className="bg-[#050816] border border-[#1E293B] rounded text-[11px] text-slate-200 px-2 py-1 outline-none focus:border-cyan-400"
              data-testid={`block-section-${att.id}`}
            >
              <option value="">End of document</option>
              {sections.map((s, i) => <option key={s.id} value={s.id}>{`${i + 1}. ${s.heading}`}</option>)}
            </select>
            <button onClick={() => onMove(att.id, -1)} disabled={index <= 1} className="text-[11px] text-slate-300 hover:text-cyan-300 disabled:opacity-30 px-1" data-testid={`block-up-${att.id}`}>↑</button>
            <button onClick={() => onMove(att.id, 1)} disabled={index >= total} className="text-[11px] text-slate-300 hover:text-cyan-300 disabled:opacity-30 px-1" data-testid={`block-down-${att.id}`}>↓</button>
          </div>
          {!isTable && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[10px] uppercase tracking-widest text-slate-500">Size</span>
              {WIDTHS.map((w) => (
                <button key={w} onClick={() => onUpdate(att.id, { width_pct: w })}
                  className={`text-[11px] px-2 py-0.5 rounded border ${pct === w ? "border-cyan-400/60 bg-cyan-500/10 text-cyan-200" : "border-white/10 text-slate-400 hover:text-slate-200"}`}
                  data-testid={`block-width-${w}-${att.id}`}>{w}%</button>
              ))}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] uppercase tracking-widest text-slate-500">Align</span>
            {[["left", AlignLeft], ["center", AlignCenter], ["right", AlignRight]].map(([a, Icon]) => (
              <button key={a} onClick={() => onUpdate(att.id, { align: a })}
                className={`p-1 rounded border ${align === a ? "border-cyan-400/60 bg-cyan-500/10 text-cyan-200" : "border-white/10 text-slate-400 hover:text-slate-200"}`}
                data-testid={`block-align-${a}-${att.id}`}><Icon size={12} /></button>
            ))}
            <button onClick={() => onDelete(att.id)} className="ml-auto text-slate-500 hover:text-red-400 p-1" data-testid={`block-del-${att.id}`}><Trash2 size={12} /></button>
          </div>
          <input
            defaultValue={att.caption || ""}
            onBlur={(e) => { if (e.target.value !== (att.caption || "")) onUpdate(att.id, { caption: e.target.value }); }}
            placeholder="Caption"
            className="w-full bg-[#050816] border border-[#1E293B] rounded text-[11px] text-slate-200 px-2 py-1 outline-none focus:border-cyan-400"
            data-testid={`block-caption-input-${att.id}`}
          />
        </div>
      )}
    </div>
  );
}

export function TypeIcon({ type, size = 12 }) {
  if (type === "table") return <TableIcon size={size} />;
  if (type === "chart") return <BarChart3 size={size} />;
  return <ImageIcon size={size} />;
}

/** Cover-page representation in the review canvas. */
export function CoverPreview({ doc, workspace }) {
  const md = doc.metadata || {};
  const rows = [
    ["Document No", doc.document_number], ["Revision", doc.revision],
    ["Effective Date", md.effective_date], ["Site", md.site], ["Department", md.department],
    ["Author", md.author || "Not assigned"], ["Reviewer", md.reviewer || "Not assigned"],
    ["Approver", md.approver || "Not assigned"],
  ].filter(([, v]) => v);
  return (
    <div className="border-b-2 border-dashed border-slate-300 pb-6 mb-6" data-testid="cover-preview">
      <div className="text-center py-6">
        <div className="mono text-[9px] uppercase tracking-widest text-slate-400">Cover page</div>
        <div style={{ fontWeight: 700, fontSize: 15, marginTop: 18 }}>{workspace?.company_name || workspace?.name || ""}</div>
        <div style={{ fontWeight: 800, fontSize: 26, margin: "10px 0" }}>{doc.title}</div>
        <div style={{ fontSize: 12, color: "#475569" }}>{doc.document_type}</div>
      </div>
      <table className="meta" style={{ width: "80%", margin: "0 auto" }}>
        <tbody>{rows.map(([k, v]) => <tr key={k}><td className="k" style={{ width: "40%" }}>{k}</td><td>{v}</td></tr>)}</tbody>
      </table>
    </div>
  );
}

/** Contents-page representation. Page numbers are intentionally not faked. */
export function ContentsPreview({ sections, title = "Contents" }) {
  return (
    <div className="border-b-2 border-dashed border-slate-300 pb-6 mb-6" data-testid="contents-preview">
      <h2 className="section">{title}</h2>
      <ol style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {sections.map((s, i) => (
          <li key={s.id} style={{ display: "flex", alignItems: "baseline", gap: 6, fontSize: 12, padding: "3px 0" }}>
            <span>{i + 1}. {s.heading}</span>
            <span style={{ flex: 1, borderBottom: "1px dotted #94a3b8", transform: "translateY(-3px)" }} />
          </li>
        ))}
      </ol>
      <div style={{ fontSize: 9, fontStyle: "italic", color: "#64748b", marginTop: 8 }}>
        Word builds the real table of contents with page numbers on export — it may ask you to update the field the first time you open the file.
      </div>
    </div>
  );
}
